import { GasStationModel } from '@core/api';

/**
 * Builds allowed return stations for a rental.
 *
 * - Always: rental station
 * - nonTransit: rental station only
 * - Otherwise: transit stations in other cities
 * - If trailer is away from origin: in origin city only the origin station
 */
export function filterReturnStations(
  stations: GasStationModel[],
  rentalStation: GasStationModel,
  originStation?: GasStationModel | null,
  nonTransit?: boolean
): GasStationModel[] {
  if (nonTransit) {
    return rentalStation ? [rentalStation] : [];
  }

  const rentalId = Number(rentalStation.id);
  const rentalGroup = normalizeGroup(rentalStation && rentalStation.location_group);
  const originId = originStation ? Number(originStation.id) : null;
  const originGroup = originStation ? normalizeGroup(originStation.location_group) : null;
  const isAwayFromOrigin = originId != null && originId !== rentalId;

  const byId: { [id: number]: GasStationModel } = {};

  (stations || []).forEach(station => {
    if (station && station.id != null) {
      byId[Number(station.id)] = station;
    }
  });

  byId[rentalId] = rentalStation;

  if (isAwayFromOrigin && originStation) {
    byId[originId] = originStation;
  }

  return Object.keys(byId)
    .map(key => byId[Number(key)])
    .filter(station => {
      const stationId = Number(station.id);

      if (stationId === rentalId) {
        return true;
      }

      if (isAwayFromOrigin && stationId === originId) {
        return true;
      }

      const stationGroup = normalizeGroup(station.location_group);

      // Same city as rental (other stations) — not allowed
      if (rentalGroup && stationGroup === rentalGroup) {
        return false;
      }

      // Origin city: only origin station
      if (isAwayFromOrigin && originGroup && stationGroup === originGroup) {
        return false;
      }

      return true;
    });
}

export function normalizeGroup(group: string | null | undefined): string | null {
  if (!group) {
    return null;
  }
  return String(group).trim() || null;
}

export function resolveOriginStation(
  originGasStationId: number | undefined | null,
  originGasStation: GasStationModel | undefined | null,
  stations: GasStationModel[] = []
): GasStationModel | null {
  if (!originGasStationId) {
    return null;
  }

  const originId = Number(originGasStationId);

  if (originGasStation && Number(originGasStation.id) === originId) {
    if (originGasStation.location_group) {
      return originGasStation;
    }
    const fromList = stations.find(s => Number(s.id) === originId);
    if (fromList && fromList.location_group) {
      return Object.assign({}, originGasStation, { location_group: fromList.location_group });
    }
    return originGasStation;
  }

  return stations.find(s => Number(s.id) === originId) || null;
}
